import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import { useAuth } from "../../auth/AuthContext";
import { Empty, ErrorNote, Loading } from "../../components/Feedback";
import { Badge, Btn, Card, ConfirmDialog, Field, Input, PasswordInput, Textarea } from "../../components/Shared";
import { focusWhenReady, rememberFocus } from "../../focus";
import { useApi } from "../../useApi";
import { LEGEND, SR_ONLY, SuccessNote, formatWhen } from "./parts";

// Management -> Users. Accounts and their permissions live in the portal (Keycloak only proves
// who somebody is): an administrator creates accounts, grants the permissions admin / curator
// (curator: may create projects) and deactivates accounts. What a person may do inside a
// project is their project role there (curator / user), given by that project's curators on
// the project page; this screen only shows each account's memberships. Somebody who signed in
// through Keycloak for the first time has no permission and is a member of nothing.
//
//   GET /api/admin/users | /admin/roles
//   POST/PATCH /api/admin/users[/{id}]            `roles` REPLACES what the account has
//   POST /api/admin/users/{id}/send-invitation    a local account: a new one-time link (30 min);
//                                                 a Keycloak account, linked or not: the sign-in page
//                                                 (409 while Keycloak sign-in is off: shown as given)
//   POST /api/admin/users/{id}/confirm-profile    submitted or incomplete -> confirmed (409 / 422 shown)
//   GET  /api/admin/users/{id}/deletion-preview   what a delete removes, keeps and what blocks it
//   DELETE /api/admin/users/{id}                  only a DEACTIVATED account, never your own; refused
//                                                 (409) while it authors a study or owns a
//                                                 hand-defined table: the dialog lists them as given
//
// Accounts (N5): the e-mail is the sign-in identifier; first and last name are required, the
// institute and the address optional. A local account is created WITHOUT a password: the person
// gets an invitation, a link to set it that works once for 30 minutes. The answer's `invitation`
// says whether it was mailed; when it was not (no mail connected, or the send failed:
// `mail_error`), `invitation.link` is the link for the admin to pass on, shown with a Copy
// button. A Keycloak account an admin creates gets an invitation too (task 2026-10-22): the
// sign-in page, no password and no expiry. A Keycloak self-registration completes its profile and submits it; it is listed under
// "Waiting for confirmation" until an admin presses "Confirm profile". An admin may also confirm
// an "Incomplete" profile its person never submitted (task 2026-10-22): the row names what is
// missing, the admin fills it in with Edit, and a dialog says the person did not submit it.
//
// Send invitation (task 2026-10-22-send-invitation-is-on-every-account) is on every active
// account with an e-mail. While it runs the button says "Sending…"; after a 200 the button
// itself says so until the page is left: green "Invitation sent ✓ HH:MM", or amber "Not mailed —
// link above" next to the InvitationNote with the link (the label changes, not only the colour),
// and the row's status text is announced. A further click asks first ("Send again"), so a double
// click sends one mail. The sent state is the component's, per account id: lost on reload.
//
// Unused accounts (task I, 2026-10-21): "Deactivate accounts unused for N days" (0 = never;
// GET/PUT /api/admin/inactivity/settings) — a daily job deactivates them silently; admins and the
// only curator of a project never. The filter "Deactivated" lists them with a checkbox each;
// "Delete selected" asks the server what blocks each one (POST /admin/users/bulk-delete-preview:
// membership in a project blocks, besides the single delete's rules), confirms by typing the
// number of accounts and deletes the deletable ones (POST /admin/users/bulk-delete {user_ids,
// confirm_count}: the server checks the typed number too); the rest are
// listed with their reasons.
//
// Every refusal (your own admin permission, a duplicate name, deactivating or taking the curator
// permission from the only curator of a project: 409 naming the projects) is the server's
// sentence, shown as it came in role="alert".

export const ROLE_LABELS = { admin: "Administrator", curator: "Curator" };
const ROLE_ORDER = ["admin", "curator"];

const table = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th = { textAlign: "left", padding: "8px 10px", fontSize: 11, fontWeight: 700, color: "#4b5563",
             textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #e5e7eb" };
const td = { padding: "8px 10px", borderBottom: "1px solid #f3f4f6", verticalAlign: "top" };

function toggle(list, value) {
  return list.includes(value) ? list.filter(v => v !== value) : [...list, value];
}

const sameSet = (a, b) => a.length === b.length && a.every(v => b.includes(v));

export const PROFILE_STATE_LABELS = { confirmed: "Confirmed", submitted: "Waiting for confirmation",
                                      incomplete: "Incomplete" };

// The required profile fields as the backend names them (services/accounts.PROFILE_REQUIRED).
const PROFILE_FIELD_LABELS = { email: "e-mail address", first_name: "first name", last_name: "last name" };

const missingLabels = u => (u.missing_profile_fields || []).map(f => PROFILE_FIELD_LABELS[f] || f);

function ProfileState({ state }) {
  const s = state || "confirmed";
  if (s === "submitted") return <Badge color="amber">{PROFILE_STATE_LABELS.submitted}</Badge>;
  if (s === "incomplete") return <Badge color="gray">{PROFILE_STATE_LABELS.incomplete}</Badge>;
  return <span>{PROFILE_STATE_LABELS.confirmed}</span>;
}

function ConfirmIncompleteDialog({ user, busy, onConfirm, onClose }) {
  const missing = missingLabels(user);
  return (
    <ConfirmDialog title={`Confirm the profile of ${user.username}?`} tone="neutral" confirmLabel="Confirm profile"
                   busy={busy} busyLabel="Confirming…"
                   blockers={missing.length ? [`Missing: ${missing.join(", ")}. Fill it in with Edit first.`] : []}
                   onConfirm={onConfirm} onClose={onClose}>
      <p style={{ margin: 0, fontSize: 14 }}>
        {user.email} signed in through Keycloak but has <strong>not submitted</strong> the profile. Confirm it
        only if you know this person: they reach the portal at once, with no permission and no project.
      </p>
    </ConfirmDialog>
  );
}

const SENT_STYLE = { background: "#ecfdf3", color: "#007a33", border: "1px solid rgba(0,150,64,0.5)" };
const NOT_MAILED_STYLE = { background: "rgba(217,119,6,0.08)", color: "#92400e", border: "1px solid rgba(217,119,6,0.45)" };

const clock = date => date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

// The Send invitation button of one row. Its accessible names: "Send invitation to <username>",
// "Sending invitation to <username>", "Invitation sent to <username> at HH:MM" and "Not mailed:
// the invitation to <username> — link above" (the ✓ and the … are not read out).
function InvitationButton({ user, sent, busy, onClick, style }) {
  const extra = sent && !busy ? (sent.mailed ? SENT_STYLE : NOT_MAILED_STYLE) : {};
  return (
    <>
      <Btn variant="secondary" onClick={onClick} disabled={busy} style={{ ...style, ...extra }}>
        {busy ? (
          <>Sending<span style={SR_ONLY}> invitation to {user.username}</span><span aria-hidden="true">…</span></>
        ) : !sent ? (
          <>Send invitation<span style={SR_ONLY}> to {user.username}</span></>
        ) : sent.mailed ? (
          <>Invitation sent<span style={SR_ONLY}> to {user.username} at</span><span aria-hidden="true"> ✓</span> {clock(sent.at)}</>
        ) : (
          <>Not mailed<span style={SR_ONLY}>: the invitation to {user.username}</span> — link above</>
        )}
      </Btn>
      <span role="status" style={SR_ONLY}>
        {sent ? (sent.mailed ? `Invitation sent to ${user.email}.` : `The invitation to ${user.email} was not mailed; the link is above.`) : ""}
      </span>
    </>
  );
}

function SendAgainDialog({ user, sent, onConfirm, onClose }) {
  return (
    <ConfirmDialog title="Send the invitation again?" tone="neutral" confirmLabel="Send again"
                   onConfirm={onConfirm} onClose={onClose}>
      <p style={{ margin: 0, fontSize: 14 }}>
        Send another invitation to {user.email}? One was sent at {clock(sent.at)}
        {sent.mailed ? "." : ", but it was not mailed."}
      </p>
    </ConfirmDialog>
  );
}

function fullName(u) {
  return [u.first_name, u.last_name].filter(Boolean).join(" ") || u.full_name;
}

function signInLabel(u) {
  if (u.auth_source === "local") return "Password";
  return u.keycloak_linked ? "Keycloak" : "Keycloak (not signed in yet)";
}

function CheckboxList({ legend, options, selected, onChange, hint }) {
  return (
    <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
      <legend style={LEGEND}>{legend}</legend>
      {options.length === 0 ? <Empty>None yet.</Empty> : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px" }}>
          {options.map(o => (
            <label key={o.value} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14 }}>
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => onChange(toggle(selected, o.value))} />
              {o.label}
            </label>
          ))}
        </div>
      )}
      {hint && <span style={{ display: "block", fontSize: 11, color: "#6b7280", marginTop: 4 }}>{hint}</span>}
    </fieldset>
  );
}

function roleOptions(roles) {
  const names = roles && roles.length ? roles.map(r => r.name) : ROLE_ORDER;
  return names.map(n => ({ value: n, label: ROLE_LABELS[n] || n }));
}

function roleHint(roles) {
  if (!roles || !roles.length) return null;
  return roles.map(r => `${ROLE_LABELS[r.name] || r.name}: ${r.description}`).join(" ");
}

function Memberships({ list }) {
  if (!list || !list.length) return <span style={{ color: "#4b5563" }}>none</span>;
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
      {list.map(m => (
        <li key={m.project_id}>
          <Link to={`/projects/${m.project_id}`}>{m.project_name}</Link>{" "}
          <span style={{ color: "#4b5563" }}>({m.role})</span>
        </li>
      ))}
    </ul>
  );
}

const PROFILE_KEYS = ["first_name", "last_name", "email", "institute", "address"];

/** The answer of a create or "Send invitation": mailed, or the link to pass on (with Copy). */
export function InvitationNote({ user, invitation }) {
  const [copied, setCopied] = useState(null);
  const inputRef = useRef(null);
  if (!invitation) return null;
  const who = user.email || user.username;
  // A Keycloak invitation (task 2026-10-22) carries the sign-in page: no password, no expiry.
  const keycloak = user.auth_source === "keycloak";
  if (invitation.mailed) {
    return (
      <SuccessNote>
        {keycloak
          ? `Invitation mailed to ${who}: the sign-in page, to sign in through Keycloak with this address.`
          : `Invitation mailed to ${who}. Its link to set a password works once, for 30 minutes.`}
      </SuccessNote>
    );
  }
  if (!invitation.link) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(invitation.link);
      setCopied("Copied to the clipboard.");
    } catch {
      inputRef.current?.select();
      setCopied("Could not copy; the link is selected, copy it with Ctrl+C.");
    }
  };
  return (
    <div data-invitation style={{ padding: "12px 14px", borderRadius: 8, background: "rgba(217,119,6,0.08)",
                                  border: "1px solid rgba(217,119,6,0.30)", fontSize: 13, color: "#1a1a1a", margin: "0 0 14px" }}>
      <p role="status" style={{ margin: "0 0 10px" }}>
        The invitation for {who} was not mailed{invitation.mail_error ? ` (${invitation.mail_error})` : ""}.
        {keycloak
          ? " Pass this sign-in address on yourself: they sign in through Keycloak with the address above."
          : " Pass this link on yourself: it works once, for 30 minutes."}
      </p>
      <Field label={`Invitation link for ${who}`}>
        <input ref={inputRef} value={invitation.link} readOnly spellCheck={false}
               style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", border: "1px solid #d1d5db",
                        borderRadius: 8, fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }} />
      </Field>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
        <Btn variant="secondary" onClick={copy}>Copy<span style={SR_ONLY}> the invitation link for {who}</span></Btn>
        {copied && <span role="status" style={{ fontSize: 12, color: "#374151" }}>{copied}</span>}
      </div>
    </div>
  );
}

function UserEditor({ user, roles, isSelf, onSaved, onClose }) {
  const [form, setForm] = useState({
    ...Object.fromEntries(PROFILE_KEYS.map(k => [k, user[k] || ""])),
    roles: user.roles, is_active: user.is_active, password: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const headingRef = useRef(null);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const emailFromKeycloak = user.auth_source === "keycloak" && user.keycloak_linked;

  // Focus to the editor when it opens, so a keyboard user lands on what they asked for.
  useEffect(() => { headingRef.current?.focus(); }, []);

  const changes = () => {
    const out = {};
    for (const k of PROFILE_KEYS) {
      const v = form[k].trim();
      if (v !== (user[k] || "")) out[k] = v || null;
    }
    if (!sameSet(form.roles, user.roles)) out.roles = form.roles;
    if (form.is_active !== user.is_active) out.is_active = form.is_active;
    if (form.password) out.password = form.password;
    return out;
  };

  const save = async e => {
    e.preventDefault();
    const payload = changes();
    if (Object.keys(payload).length === 0) { onClose(null); return; }
    const restore = rememberFocus();
    setBusy(true);
    setError(null);
    try {
      const updated = await api.patch(`/admin/users/${user.id}`, payload);
      onSaved(updated);
    } catch (err) {
      setError(err);
      setBusy(false);
      restore();
    }
  };

  return (
    <Card style={{ margin: "16px 0", padding: "20px 24px", borderColor: "rgba(0,150,64,0.4)" }}>
      <form onSubmit={save} aria-label={`Edit ${user.username}`} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <h3 ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
          Edit {user.username}
        </h3>
        <ErrorNote error={error} />
        <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          <Field label="First name" required><Input value={form.first_name} onChange={set("first_name")} autoComplete="off" /></Field>
          <Field label="Last name" required><Input value={form.last_name} onChange={set("last_name")} autoComplete="off" /></Field>
          <Field label="E-mail" required={!(user.auth_source === "local" && user.roles.includes("admin"))}
                 hint={emailFromKeycloak ? "The sign-in identifier; Keycloak keeps it, change it there."
                   : user.auth_source === "keycloak"
                     ? "The sign-in identifier. Linked on the first Keycloak sign-in with a verified address equal to this."
                     : "The sign-in identifier."}>
            <Input type="email" value={form.email} onChange={set("email")} readOnly={emailFromKeycloak} autoComplete="off" />
          </Field>
          <Field label="Institute"><Input value={form.institute} onChange={set("institute")} autoComplete="off" /></Field>
          {user.auth_source === "local" && (
            <Field label="New password" hint="At least 12 characters. Blank keeps the current one; or send an invitation.">
              <PasswordInput value={form.password} onChange={set("password")} autoComplete="new-password" />
            </Field>
          )}
        </div>
        <Field label="Address"><Textarea value={form.address} onChange={set("address")} autoComplete="off" /></Field>
        <CheckboxList legend="Permissions" options={roleOptions(roles)} selected={form.roles}
                      onChange={v => setForm(f => ({ ...f, roles: v }))}
                      hint={[roleHint(roles), isSelf ? "You cannot take the administrator permission from your own account." : null].filter(Boolean).join(" ")} />
        <div style={{ fontSize: 14 }}>
          <div style={LEGEND}>Project memberships</div>
          <Memberships list={user.memberships} />
          <span style={{ display: "block", fontSize: 11, color: "#6b7280", marginTop: 4 }}>
            Given by each project's curators on the project page. The only curator of a project
            cannot be deactivated or lose the curator permission until another curator is named there.
          </span>
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14 }}>
          <input type="checkbox" checked={form.is_active} disabled={isSelf}
                 onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
          Account active {isSelf && <span style={{ color: "#6b7280", fontSize: 12 }}>(you cannot deactivate yourself)</span>}
        </label>
        <div style={{ display: "flex", gap: 10 }}>
          <Btn type="submit" disabled={busy}>{busy ? "Saving…" : "Save"}</Btn>
          <Btn variant="secondary" onClick={() => onClose(null)}>Cancel</Btn>
        </div>
      </form>
    </Card>
  );
}

const SIGN_IN_OPTIONS = [
  { value: "local", label: "Portal password (the person is invited by e-mail to set it)" },
  { value: "keycloak", label: "Keycloak" },
];

function NewUserForm({ roles, onCreated, onCancel }) {
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", institute: "", address: "",
                                     auth_source: "local", roles: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const ready = form.email.trim() && form.first_name.trim() && form.last_name.trim();

  const submit = async e => {
    e.preventDefault();
    const restore = rememberFocus();
    setBusy(true);
    setError(null);
    const body = { email: form.email.trim(), first_name: form.first_name.trim(), last_name: form.last_name.trim(),
                   auth_source: form.auth_source, roles: form.roles };
    if (form.institute.trim()) body.institute = form.institute.trim();
    if (form.address.trim()) body.address = form.address.trim();
    try {
      onCreated(await api.post("/admin/users", body));
    } catch (err) {
      setError(err);
      setBusy(false);
      restore();
    }
  };

  return (
    <Card style={{ margin: "0 0 16px", padding: "20px 24px" }}>
      <form onSubmit={submit} aria-label="New user" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>New user</h3>
        <p style={{ margin: 0, fontSize: 13, color: "#4b5563" }}>
          The e-mail address is the account's sign-in identifier. A portal-password account gets an
          invitation: a link to set its password, which works once for 30 minutes. A Keycloak
          account is linked on the first Keycloak sign-in with that verified address. People who
          sign in through Keycloak with self-registration on get an account by themselves, which
          you confirm here once they have submitted their profile.
        </p>
        <ErrorNote error={error} />
        <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          <Field label="E-mail" required><Input type="email" value={form.email} onChange={set("email")} autoComplete="off" /></Field>
          <Field label="First name" required><Input value={form.first_name} onChange={set("first_name")} autoComplete="off" /></Field>
          <Field label="Last name" required><Input value={form.last_name} onChange={set("last_name")} autoComplete="off" /></Field>
          <Field label="Institute"><Input value={form.institute} onChange={set("institute")} autoComplete="off" /></Field>
        </div>
        <Field label="Address"><Textarea value={form.address} onChange={set("address")} autoComplete="off" /></Field>
        <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
          <legend style={LEGEND}>Sign-in</legend>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px" }}>
            {SIGN_IN_OPTIONS.map(o => (
              <label key={o.value} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14 }}>
                <input type="radio" name="new-user-sign-in" value={o.value} checked={form.auth_source === o.value}
                       onChange={() => setForm(f => ({ ...f, auth_source: o.value }))} />
                {o.label}
              </label>
            ))}
          </div>
        </fieldset>
        <CheckboxList legend="Permissions" options={roleOptions(roles)} selected={form.roles}
                      onChange={v => setForm(f => ({ ...f, roles: v }))} hint={roleHint(roles)} />
        <div style={{ display: "flex", gap: 10 }}>
          <Btn type="submit" disabled={busy || !ready}>{busy ? "Creating…" : "Create user"}</Btn>
          <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
        </div>
      </form>
    </Card>
  );
}

function CountList({ counts, empty }) {
  const entries = Object.entries(counts || {});
  if (!entries.length) return <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>{empty}</p>;
  return (
    <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 13 }}>
      {entries.map(([label, n]) => <li key={label}>{n} {label}</li>)}
    </ul>
  );
}

/** Delete a deactivated account: the server's preview (what goes, what stays, and each blocker
 *  as the server words it), then type the username to confirm. A 409 of the DELETE itself
 *  (something changed meanwhile) is the server's sentence, shown as it came. */
function DeleteUserDialog({ user, onDeleted, onClose }) {
  const preview = useApi(`/admin/users/${user.id}/deletion-preview`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const p = preview.data;

  const confirm = async () => {
    if (!p || p.blockers.length) return;
    setBusy(true);
    setError(null);
    try {
      await api.del(`/admin/users/${user.id}`);
      onDeleted(user);
    } catch (err) {
      setError(err);
      setBusy(false);
      preview.reload();
    }
  };

  return (
    <ConfirmDialog title={`Delete ${user.username}?`} confirmWord={user.username} confirmLabel="Delete account"
                   busy={busy} blockers={p ? p.blockers.map(b => b.text) : []}
                   onConfirm={confirm} onClose={onClose}>
      <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
        The account is removed for good: it disappears from Users, the member pickers and the API,
        and its e-mail address can be used for a new account. This cannot be undone.
      </p>
      <ErrorNote error={error || preview.error} />
      {!p ? <Loading /> : (
        <>
          {p.refusal && (
            <p data-refusal style={{ margin: "12px 0 0", fontSize: 13, color: "#92400e" }}>{p.refusal}</p>
          )}
          <div style={{ marginTop: 12 }}>
            <div style={LEGEND}>Removed with it</div>
            <CountList counts={p.removed} empty="Nothing else." />
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={LEGEND}>Kept, without the link to the account</div>
            <CountList counts={p.kept} empty="Nothing." />
            <span style={{ display: "block", fontSize: 11, color: "#6b7280", marginTop: 4 }}>
              The audit trail keeps the name {user.username}.
            </span>
          </div>
        </>
      )}
    </ConfirmDialog>
  );
}

/** "Deactivate accounts unused for N days": the job deactivate-unused-accounts (task I). */
export function InactivitySettings() {
  const [days, setDays] = useState("");
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get("/admin/inactivity/settings").then(s => setDays(String(s.accounts_inactive_after_days))).catch(setError);
  }, []);
  const save = async e => {
    e.preventDefault();
    setError(null);
    setSaved(null);
    try {
      const s = await api.put("/admin/inactivity/settings", { accounts_inactive_after_days: Number(days || 0) });
      setDays(String(s.accounts_inactive_after_days));
      setSaved(s.accounts_inactive_after_days);
    } catch (err) { setError(err); }
  };
  return (
    <Card style={{ marginBottom: 18 }}>
      <h2 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700 }}>Unused accounts</h2>
      <p style={{ fontSize: 13, color: "#4b5563", margin: "0 0 12px" }}>
        Once a day, an account with no activity for longer than this (no portal sign-in, no use of its
        project API tokens or SQL logins) is deactivated, without an e-mail: its SQL logins are dropped and
        its sessions end. Administrators are never deactivated this way; the only curator of a project is
        kept, and you are told in the bell. An administrator activates an account again here.
      </p>
      <ErrorNote error={error} />
      <form onSubmit={save} aria-label="Unused accounts" data-field-row
            style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Field label="Deactivate accounts unused for (days, 0 = never)">
          <Input type="number" min="0" value={days} onChange={e => setDays(e.target.value)} />
        </Field>
        <Btn type="submit" variant="secondary">Save</Btn>
      </form>
      {saved !== null && <p role="status" style={{ fontSize: 13, color: "#065f46" }}>
        {saved === 0 ? "Unused accounts stay active." : `Accounts unused for more than ${saved} days are deactivated.`}</p>}
    </Card>
  );
}

/** Delete several deactivated accounts: the server's preview per account (what blocks it), one
 *  confirmation by typing how many will go, then the result: deleted, and refused with reasons. */
function BulkDeleteDialog({ ids, onDone, onClose }) {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.post("/admin/users/bulk-delete-preview", { user_ids: ids }).then(setPreview).catch(setError);
  }, [ids]);
  const ok = preview ? preview.filter(p => p.can_delete) : [];
  const blocked = preview ? preview.filter(p => !p.can_delete) : [];
  const n = ok.length;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post("/admin/users/bulk-delete", { user_ids: ok.map(p => p.id), confirm_count: n });
      setResult(r);
      onDone(r);
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  if (result) {
    return (
      <ConfirmDialog title="Accounts deleted" tone="neutral" confirmLabel="Close" onConfirm={onClose} onClose={onClose}>
        <p role="status" style={{ margin: 0, fontSize: 14 }}>
          {result.deleted.length} account{result.deleted.length === 1 ? "" : "s"} deleted
          {result.deleted.length ? `: ${result.deleted.map(d => d.username).join(", ")}` : ""}.
        </p>
        {result.refused.length > 0 && <RefusedList title="Not deleted" items={result.refused} />}
      </ConfirmDialog>
    );
  }
  return (
    <ConfirmDialog title={`Delete ${ids.length} selected account${ids.length === 1 ? "" : "s"}?`}
                   confirmWord={n ? String(n) : undefined}
                   confirmLabel={`Delete ${n} account${n === 1 ? "" : "s"}`} busy={busy}
                   blockers={preview && n === 0 ? ["None of the selected accounts can be deleted now; the reasons are listed above."] : []}
                   onConfirm={confirm} onClose={onClose}>
      <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
        Each account is removed for good, as with Delete on its row. An account still a member of a project
        is not deleted: remove it from the project first. This cannot be undone.
      </p>
      <ErrorNote error={error} />
      {!preview ? (!error && <Loading />) : (
        <>
          {n > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={LEGEND}>Deleted ({n})</div>
              <p data-bulk-ok style={{ margin: 0, fontSize: 13 }}>{ok.map(p => p.username).join(", ")}</p>
            </div>
          )}
          {blocked.length > 0 && <RefusedList title={`Kept (${blocked.length})`} items={blocked} />}
          {n > 0 && <p style={{ margin: "12px 0 0", fontSize: 13 }}>Type the number of accounts to delete.</p>}
        </>
      )}
    </ConfirmDialog>
  );
}

function RefusedList({ title, items }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={LEGEND}>{title}</div>
      <ul data-bulk-refused style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
        {items.map(p => (
          <li key={p.id}><strong>{p.username || `#${p.id}`}</strong>: {p.blockers.map(b => b.text).join("; ") || p.refusal}</li>
        ))}
      </ul>
    </div>
  );
}

function UsersPanel({ users, roles, onChanged }) {
  const { user: me } = useAuth();
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [invited, setInvited] = useState(null);       // {user, invitation}
  const [busy, setBusy] = useState(null);             // the id an action runs for
  const [filter, setFilter] = useState("all");
  const [deleting, setDeleting] = useState(null);     // the account the Delete dialog is for
  const [confirming, setConfirming] = useState(null); // the incomplete account the Confirm dialog is for
  const [sent, setSent] = useState({});               // {user id: {at, mailed}}: invitations sent on this page
  const [again, setAgain] = useState(null);           // the account the Send again dialog is for
  const listRef = useRef(null);

  const [selected, setSelected] = useState([]);      // deactivated accounts ticked for a bulk delete
  const [bulk, setBulk] = useState(null);            // the ids the bulk dialog is for
  const waiting = users.filter(u => u.profile_state === "submitted");
  const inactive = users.filter(u => !u.is_active && !(me && me.id === u.id));
  const shown = filter === "submitted" ? waiting : filter === "inactive" ? inactive : users;
  const selectable = filter === "inactive";
  const ticked = selected.filter(id => inactive.some(u => u.id === id));

  const backTo = id => focusWhenReady(() => listRef.current
    && (listRef.current.querySelector(`[data-focus-return="user-${id}"]`) || listRef.current.querySelector("button")));

  const clear = () => { setNotice(null); setError(null); setInvited(null); };

  const close = (id, message) => {
    setEditing(null);
    if (message) setNotice(message);
    backTo(id);
  };

  const created = u => {
    setCreating(false);
    setNotice(`User ${u.username} created.`
      + (u.auth_source === "keycloak" ? ` It is linked on the first Keycloak sign-in with the verified address ${u.email}.` : ""));
    if (u.invitation) setInvited({ user: u, invitation: u.invitation });
    onChanged();
    backTo(u.id);
  };

  const act = async (u, path, done) => {
    clear();
    setBusy(u.id);
    try {
      done(await api.post(`/admin/users/${u.id}/${path}`));
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const sendInvitation = u => act(u, "send-invitation", r => {
    setInvited({ user: r, invitation: r.invitation });
    setSent(s => ({ ...s, [u.id]: { at: new Date(), mailed: !!(r.invitation && r.invitation.mailed) } }));
  });
  const confirmProfile = u => act(u, "confirm-profile", r => setNotice(`The profile of ${r.username} is confirmed.`));

  const small = { padding: "5px 12px", fontSize: 13 };

  return (
    <section aria-labelledby="admin-users" ref={listRef} style={{ marginBottom: 32 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
        <h2 id="admin-users" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Accounts</h2>
        {!creating && <Btn variant="secondary" onClick={() => { setCreating(true); clear(); }}>New user</Btn>}
      </div>
      <ErrorNote error={error} />
      <SuccessNote>{notice}</SuccessNote>
      {invited && <InvitationNote user={invited.user} invitation={invited.invitation} />}
      {creating && (
        <NewUserForm roles={roles}
                     onCancel={() => { setCreating(false); backTo(null); }}
                     onCreated={created} />
      )}
      <fieldset style={{ border: "none", padding: 0, margin: "0 0 12px" }}>
        <legend style={LEGEND}>Show</legend>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px" }}>
          {[["all", "All accounts"], ["submitted", `Waiting for confirmation (${waiting.length})`],
            ["inactive", `Deactivated (${inactive.length})`]].map(([v, label]) => (
            <label key={v} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14 }}>
              <input type="radio" name="users-filter" value={v} checked={filter === v} onChange={() => setFilter(v)} />
              {label}
            </label>
          ))}
        </div>
      </fieldset>
      {selectable && inactive.length > 0 && (
        <div data-page-actions style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", margin: "0 0 12px" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14 }}>
            <input type="checkbox" checked={ticked.length === inactive.length}
                   onChange={() => setSelected(ticked.length === inactive.length ? [] : inactive.map(u => u.id))} />
            Select all deactivated
          </label>
          <Btn variant="danger" disabled={ticked.length === 0} onClick={() => { clear(); setBulk(ticked); }}>
            Delete selected ({ticked.length})
          </Btn>
        </div>
      )}
      {shown.length === 0 ? (
        <Empty>{filter === "submitted" ? "No profile is waiting for confirmation."
          : filter === "inactive" ? "No account is deactivated." : "No users yet."}</Empty>
      ) : (
        <div style={{ overflowX: "auto", position: "relative" }}>
          {/* position: relative contains the visually hidden (position: absolute) words of the
              Actions cells; without it they escaped the scroll box and the page scrolled
              sideways by 112 px at 1280 px (a11y check of 2026-10-21). */}
          <table className="responsive-table" style={table}>
            <caption style={{ textAlign: "left", fontSize: 12, color: "#4b5563", paddingBottom: 6 }}>
              {filter === "submitted"
                ? `${shown.length} account${shown.length === 1 ? "" : "s"} waiting for confirmation.`
                : filter === "inactive"
                ? `${shown.length} deactivated account${shown.length === 1 ? "" : "s"}. Tick the ones to delete.`
                : `${shown.length} account${shown.length === 1 ? "" : "s"}. An account sees only the projects it is a member of; an administrator sees every project.`}
            </caption>
            <thead>
              <tr>
                {selectable && <th scope="col" style={th}><span style={SR_ONLY}>Select</span></th>}
                <th scope="col" style={th}>User</th>
                <th scope="col" style={th}>Profile</th>
                <th scope="col" style={th}>Sign-in</th>
                <th scope="col" style={th}>Permissions</th>
                <th scope="col" style={th}>Projects</th>
                <th scope="col" style={th}>Last sign-in</th>
                <th scope="col" style={th}>Last active</th>
                <th scope="col" style={th}><span style={SR_ONLY}>Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {shown.map(u => (
                <tr key={u.id} data-user={u.username}>
                  {selectable && (
                    <td style={td}>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <input type="checkbox" checked={selected.includes(u.id)}
                               onChange={() => setSelected(s => toggle(s, u.id))} />
                        <span style={SR_ONLY}>Select {u.username}</span>
                      </label>
                    </td>
                  )}
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{u.username}</div>
                    <div style={{ color: "#4b5563" }}>{[fullName(u), u.email].filter(Boolean).join(" · ")}</div>
                    {u.institute && <div style={{ color: "#4b5563" }}>{u.institute}</div>}
                    {!u.is_active && <Badge color="red"><span aria-hidden="true">✕ </span>Deactivated</Badge>}
                  </td>
                  <td data-label="Profile" style={td}>
                    <ProfileState state={u.profile_state} />
                    {u.profile_state === "incomplete" && missingLabels(u).length > 0 && (
                      <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>
                        missing: {missingLabels(u).join(", ")}
                      </div>
                    )}
                    {u.profile_state === "submitted" && u.confirming_curator && (
                      <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>
                        named {u.confirming_curator.full_name || u.confirming_curator.username} to confirm it
                      </div>
                    )}
                  </td>
                  <td data-label="Sign-in" style={td}>{signInLabel(u)}</td>
                  <td data-label="Permissions" style={td}>
                    {u.roles.length ? u.roles.map(r => ROLE_LABELS[r] || r).join(", ")
                                    : <span style={{ color: "#4b5563" }}>none</span>}
                  </td>
                  <td data-label="Projects" style={td}><Memberships list={u.memberships} /></td>
                  <td data-label="Last sign-in" style={td}>{formatWhen(u.last_login_at) || <span style={{ color: "#4b5563" }}>never</span>}</td>
                  <td data-label="Last active" style={td}>{formatWhen(u.last_active_at) || <span style={{ color: "#4b5563" }}>never</span>}</td>
                  <td style={td} data-focus-return={`user-${u.id}`}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end" }}>
                      {u.profile_state === "submitted" && (
                        <Btn onClick={() => confirmProfile(u)} disabled={busy === u.id} style={small}>
                          Confirm profile<span style={SR_ONLY}> of {u.username}</span>
                        </Btn>
                      )}
                      {u.profile_state === "incomplete" && u.is_active && (
                        <Btn variant="secondary" onClick={() => { clear(); setConfirming(u); }} disabled={busy === u.id} style={small}>
                          Confirm profile<span style={SR_ONLY}> of {u.username}</span>
                        </Btn>
                      )}
                      {u.email && u.is_active && (
                        <InvitationButton user={u} sent={sent[u.id]} busy={busy === u.id} style={small}
                                          onClick={() => { if (!sent[u.id]) sendInvitation(u); else { clear(); setAgain(u); } }} />
                      )}
                      <Btn variant="ghost" onClick={() => { setEditing(u.id); clear(); }} style={small}>
                        Edit<span style={SR_ONLY}> {u.username}</span>
                      </Btn>
                      {!u.is_active && !(me && me.id === u.id) && (
                        <Btn variant="danger" onClick={() => { setDeleting(u); clear(); }} style={small}>
                          Delete<span style={SR_ONLY}> {u.username}</span>
                        </Btn>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing != null && users.find(u => u.id === editing) && (
        <UserEditor key={editing} user={users.find(u => u.id === editing)} roles={roles}
                    isSelf={me && me.id === editing}
                    onSaved={u => { onChanged(); close(u.id, `User ${u.username} saved.`); }}
                    onClose={() => close(editing, null)} />
      )}
      {confirming && (
        <ConfirmIncompleteDialog user={users.find(u => u.id === confirming.id) || confirming}
                                 busy={busy === confirming.id}
                                 onClose={() => { const id = confirming.id; setConfirming(null); backTo(id); }}
                                 onConfirm={() => {
                                   const u = confirming;
                                   setConfirming(null);
                                   confirmProfile(u).then(() => backTo(u.id));
                                 }} />
      )}
      {again && sent[again.id] && (
        <SendAgainDialog user={again} sent={sent[again.id]}
                         onClose={() => { const id = again.id; setAgain(null); backTo(id); }}
                         onConfirm={() => {
                           const u = again;
                           setAgain(null);
                           sendInvitation(u).then(() => backTo(u.id));
                         }} />
      )}
      {bulk && (
        <BulkDeleteDialog ids={bulk} onClose={() => { setBulk(null); backTo(null); }}
                          onDone={r => {
                            setSelected(s => s.filter(id => !r.deleted.some(d => d.id === id)));
                            if (r.deleted.length) setNotice(`${r.deleted.length} account${r.deleted.length === 1 ? "" : "s"} deleted.`);
                            onChanged();
                          }} />
      )}
      {deleting && (
        <DeleteUserDialog user={deleting} onClose={() => setDeleting(null)}
                          onDeleted={u => {
                            setDeleting(null);
                            setEditing(e => (e === u.id ? null : e));
                            setNotice(`User ${u.username} deleted.`);
                            onChanged();
                            backTo(null);
                          }} />
      )}
    </section>
  );
}

export default function Users() {
  const users = useApi("/admin/users");
  const roles = useApi("/admin/roles");

  if (users.error) return <ErrorNote error={users.error} />;
  if (!users.data) return <Loading />;
  return (
    <>
      <UsersPanel users={users.data} roles={roles.data || []} onChanged={users.reload} />
      <InactivitySettings />
    </>
  );
}
